import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useParams, useNavigate, useLocation } from "react-router-dom";
import { useReduxStore } from "../../hooks/useReduxStore";
import { GetPlaylistDetailAction } from "../../store/Actions/action";
import { updateItem } from "../../store/Reducers/itemReducer";
import { updateHome } from "../../store/Reducers/homeReducer";
import { isUserSubscribed, getItemAccessType } from "../../helper/accessTypeHelper";
import useAdGate from "../../hooks/useAdGate";
import { toast } from "react-toastify";
import { toastConfiguration } from "../../Constant/Data";

export default function usePlaylistDetail() {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const playerRef = useRef(null);

  // Extract playlist ID from URL params, query string, or history state
  const playlistId = params.id || searchParams.get("id") || location.state?.id || 1;

  const { dispatch, getState } = useReduxStore();
  const { playlistDetail, playlistDetailLoading } = getState("Item");
  const { userData, token } = getState("AUTH");

  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlayerVisible, setIsPlayerVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [featuredVideoModal, setFeaturedVideoModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    actionType: "listen",
    title: "Sign In to Listen",
    message: "Please sign in to stream curated playlist tracks on BAB Digital.",
    itemTitle: "",
  });

  const handleLoginModal = useCallback((config) => {
    if (typeof config === "string") {
      setModalConfig((prev) => ({
        ...prev,
        isOpen: true,
        actionType: config,
      }));
    } else if (config && typeof config === "object") {
      setModalConfig((prev) => ({
        ...prev,
        isOpen: config.isOpen !== undefined ? config.isOpen : true,
        actionType: config.actionType || "listen",
        title: config.title || prev.title,
        message: config.message || prev.message,
        itemTitle: config.itemTitle || "",
      }));
    } else {
      setModalConfig((prev) => ({
        ...prev,
        isOpen: !prev.isOpen,
      }));
    }
  }, []);

  const currentPlaylistPath = `/Playlist?id=${playlistId}`;

  const onLoginClick = useCallback(() => {
    setModalConfig((prev) => ({ ...prev, isOpen: false }));
    navigate("/Login", { state: { route: currentPlaylistPath } });
  }, [navigate, currentPlaylistPath]);

  const onSignupClick = useCallback(() => {
    setModalConfig((prev) => ({ ...prev, isOpen: false }));
    navigate("/Signup", { state: { route: currentPlaylistPath } });
  }, [navigate, currentPlaylistPath]);

  const {
    isAdModalOpen,
    adData,
    triggerWithAdGate,
    onAdCompleted,
    closeAdModal,
  } = useAdGate();

  // Load playlist details on mount or playlistId change
  useEffect(() => {
    if (playlistId) {
      dispatch(GetPlaylistDetailAction({ id: playlistId }));
    }
  }, [playlistId, dispatch]);

  const tracks = playlistDetail?.products || [];
  const currentTrack = tracks[currentTrackIndex] || null;

  const toggleFeaturedVideoModal = () => {
    setFeaturedVideoModal(!featuredVideoModal);
  };

  /**
   * Loads and begins playback of a specific track with full access verification
   */
  const handlePlayTrack = useCallback(
    (track, index) => {
      if (!track) return;

      // Mandatory Authentication Gate
      if (!token) {
        handleLoginModal({
          actionType: "listen",
          title: "Sign In to Listen",
          message: `Please sign in to stream "${track?.title || "this track"}". Free accounts get instant access!`,
          itemTitle: track?.title,
        });
        return;
      }

      const access = getItemAccessType(track);
      const userIsSubscribed = isUserSubscribed(userData);

      // Access permission verification for VIP / Exclusive releases
      if (access.isExclusive && !userIsSubscribed) {
        toast.info(
          "This track is reserved for BAB VIP members. Please subscribe to listen.",
          toastConfiguration
        );
        navigate("/Subscription");
        return;
      }

      // PPV Access check
      if (access.isPpv && !track.is_purchased) {
        toast.info(
          `This track requires Pay-Per-View access ($${access.formattedPrice}).`,
          toastConfiguration
        );
        dispatch(updateItem({ itemDetail: track }));
        dispatch(updateHome({ selectedPackage: null }));
        navigate("/Checkout", { state: { item: track, isSubscription: false } });
        return;
      }

      // Ad gate flow for free tracks / non-subscribers
      triggerWithAdGate(track, () => {
        setCurrentTrackIndex(index);
        setIsPlayerVisible(true);
        setIsPlaying(true);
        dispatch(
          updateItem({
            currentPlaylistTrack: track,
            isPlaylistPlaying: true,
          })
        );

        // Ensure audio element starts playing safely
        setTimeout(() => {
          const audioElement = playerRef.current?.audio?.current;
          if (audioElement) {
            audioElement.play().catch(() => {});
          }
        }, 120);
      });
    },
    [userData, token, handleLoginModal, triggerWithAdGate, navigate, dispatch]
  );

  /**
   * Unified Toggle: If the clicked track is already the active track, toggle play/pause;
   * otherwise, load and start playing the new track.
   */
  const togglePlayPause = useCallback(
    (track, index) => {
      if (!track) return;

      const isCurrentActive =
        isPlayerVisible &&
        currentTrack &&
        (currentTrack.id === track.id || currentTrackIndex === index);

      if (isCurrentActive) {
        const audioElement = playerRef.current?.audio?.current;
        if (audioElement) {
          if (audioElement.paused || !isPlaying) {
            audioElement
              .play()
              .then(() => {
                setIsPlaying(true);
                dispatch(updateItem({ isPlaylistPlaying: true }));
              })
              .catch(() => {});
          } else {
            audioElement.pause();
            setIsPlaying(false);
            dispatch(updateItem({ isPlaylistPlaying: false }));
          }
        }
      } else {
        handlePlayTrack(track, index);
      }
    },
    [isPlayerVisible, currentTrack, currentTrackIndex, isPlaying, handlePlayTrack, dispatch]
  );

  /**
   * Main Hero Action Button: Play All / Pause / Resume
   */
  const handlePlayAll = useCallback(() => {
    if (tracks.length === 0) return;

    if (isPlayerVisible && currentTrack) {
      const audioElement = playerRef.current?.audio?.current;
      if (audioElement) {
        if (audioElement.paused || !isPlaying) {
          audioElement
            .play()
            .then(() => {
              setIsPlaying(true);
              dispatch(updateItem({ isPlaylistPlaying: true }));
            })
            .catch(() => {});
        } else {
          audioElement.pause();
          setIsPlaying(false);
          dispatch(updateItem({ isPlaylistPlaying: false }));
        }
      }
    } else {
      handlePlayTrack(tracks[0], 0);
    }
  }, [tracks, isPlayerVisible, currentTrack, isPlaying, handlePlayTrack, dispatch]);

  /**
   * Shuffles track order and starts playing
   */
  const handleShuffle = useCallback(() => {
    if (tracks.length > 1) {
      const randomIndex = Math.floor(Math.random() * tracks.length);
      handlePlayTrack(tracks[randomIndex], randomIndex);
    }
  }, [tracks, handlePlayTrack]);

  /**
   * Skip to Next Track
   */
  const handleNextTrack = useCallback(() => {
    if (tracks.length === 0) return;
    const nextIndex = (currentTrackIndex + 1) % tracks.length;
    handlePlayTrack(tracks[nextIndex], nextIndex);
  }, [tracks, currentTrackIndex, handlePlayTrack]);

  /**
   * Skip to Previous Track
   */
  const handlePrevTrack = useCallback(() => {
    if (tracks.length === 0) return;
    const prevIndex = (currentTrackIndex - 1 + tracks.length) % tracks.length;
    handlePlayTrack(tracks[prevIndex], prevIndex);
  }, [tracks, currentTrackIndex, handlePlayTrack]);

  /**
   * Auto-advance on track completion
   */
  const handleTrackEnded = useCallback(() => {
    if (currentTrackIndex + 1 < tracks.length) {
      handleNextTrack();
    } else {
      setIsPlaying(false);
      dispatch(updateItem({ isPlaylistPlaying: false }));
    }
  }, [currentTrackIndex, tracks.length, handleNextTrack, dispatch]);

  /**
   * Voluntary dismissal of the audio player bar
   */
  const handleClosePlayer = useCallback(() => {
    const audioElement = playerRef.current?.audio?.current;
    if (audioElement) {
      try {
        audioElement.pause();
      } catch (_) {}
    }
    setIsPlaying(false);
    setIsPlayerVisible(false);
    dispatch(updateItem({ isPlaylistPlaying: false }));
  }, [dispatch]);

  // Synchronize onPlay and onPause from AudioPlayer DOM events
  const onAudioPlay = useCallback(() => {
    setIsPlaying(true);
    dispatch(updateItem({ isPlaylistPlaying: true }));
  }, [dispatch]);

  const onAudioPause = useCallback(() => {
    // Keep isPlayerVisible = true! Only update isPlaying to false.
    setIsPlaying(false);
    dispatch(updateItem({ isPlaylistPlaying: false }));
  }, [dispatch]);

  // Auto-play if ?play=true is in URL or state
  useEffect(() => {
    const shouldAutoPlay =
      searchParams.get("play") === "true" || location.state?.autoPlay === true;
    if (shouldAutoPlay && tracks.length > 0 && !isPlayerVisible) {
      handlePlayTrack(tracks[0], 0);
    }
  }, [tracks, searchParams, location.state, isPlayerVisible, handlePlayTrack]);

  return {
    playlistDetail,
    playlistDetailLoading,
    tracks,
    currentTrack,
    currentTrackIndex,
    isPlayerVisible,
    setIsPlayerVisible,
    isPlaying,
    setIsPlaying,
    playerRef,
    togglePlayPause,
    handlePlayTrack,
    handlePlayAll,
    handleShuffle,
    handleNextTrack,
    handlePrevTrack,
    handleTrackEnded,
    handleClosePlayer,
    onAudioPlay,
    onAudioPause,
    featuredVideoModal,
    toggleFeaturedVideoModal,
    // AdGate modal states
    isAdModalOpen,
    adData,
    onAdCompleted,
    closeAdModal,
    // Auth Modal states
    loginModal: modalConfig.isOpen,
    modalConfig,
    handleLoginModal,
    onLoginClick,
    onSignupClick,
  };
}
